import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Mail, Lock, User, Phone } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import SocialLoginButtons from '../components/common/SocialLoginButtons';

const Login = () => {
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const { t } = useTranslation();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    phone: '',
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        const loggedUser = await login(formData.email, formData.password);
        toast.success('Login realizado com sucesso!');
        
        if (loggedUser.role === 'admin') {
          navigate('/admin/dashboard');
        } else if (['kiosk_owner', 'kiosk_staff'].includes(loggedUser.role)) {
          navigate('/partner/dashboard');
        } else if (loggedUser.role === 'city_hall') {
          navigate('/cityhall/dashboard');
        } else if (loggedUser.role === 'brand') {
          navigate('/brand/dashboard');
        } else {
          navigate('/');
        }
      } else {
        // Register new customer
        await register({
          email: formData.email,
          password: formData.password,
          full_name: formData.full_name,
          phone: formData.phone,
          role: 'customer'
        });
        toast.success('Cadastro realizado! Fazendo login...');
        const loggedUser = await login(formData.email, formData.password);
        navigate('/');
      }
    } catch (error) {
      toast.error(
        error.response?.data?.detail || 'Erro ao fazer login. Verifique suas credenciais.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/10 via-background to-secondary/10 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img
            src="/beachpay-icon.jpg"
            alt="Beach Pay"
            className="w-24 h-24 mx-auto mb-4"
          />
          <h1 className="font-['Outfit'] text-3xl font-bold text-foreground">
            {t('login.title')}
          </h1>
          <p className="text-muted-foreground mt-2">
            {t('login.subtitle')}
          </p>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <div className="flex gap-2 mb-6">
            <button
              data-testid="login-tab"
              onClick={() => setIsLogin(true)}
              className={`flex-1 py-2 rounded-lg font-semibold transition-colors ${
                isLogin
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground'
              }`}
            >
              {t('login.login')}
            </button>
            <button
              data-testid="register-tab"
              onClick={() => setIsLogin(false)}
              className={`flex-1 py-2 rounded-lg font-semibold transition-colors ${
                !isLogin
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground'
              }`}
            >
              {t('login.register')}
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    {t('login.fullName')}
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <input
                      type="text"
                      name="full_name"
                      value={formData.full_name}
                      onChange={handleChange}
                      className="w-full pl-10 pr-4 py-3 border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
                      required={!isLogin}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    {t('login.phone')}
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+55 48 99999-9999"
                      className="w-full pl-10 pr-4 py-3 border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
                      required={!isLogin}
                    />
                  </div>
                </div>
              </>
            )}

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                {t('login.email')}
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <input
                  data-testid="email-input"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full pl-10 pr-4 py-3 border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                {t('login.password')}
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <input
                  data-testid="password-input"
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className="w-full pl-10 pr-4 py-3 border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
                  required
                />
              </div>
            </div>

            <button
              data-testid="submit-button"
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-primary text-primary-foreground rounded-full font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 active:scale-95"
            >
              {loading ? t('login.wait') : isLogin ? t('login.submit') : t('login.submitRegister')}
            </button>
          </form>

          {isLogin && (
            <div className="mt-4 text-center">
              <p className="text-sm text-muted-foreground">
                {t('login.testUsers')}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Cliente: cliente@teste.com / cliente123
              </p>
            </div>
          )}

          <SocialLoginButtons />
        </div>

        <div className="mt-6 text-center">
          <Link to="/" className="text-sm text-primary hover:underline">
            {t('login.backToHome')}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Login;
